// Marketplace Produits - Design Pareto 80/20
// Sidebar filtres à gauche (desktop) + recherche autocomplete 3 niveaux

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, ShoppingCart, Package, Wrench, Zap, Droplets, 
  Flame, Home, Layers, X, Truck, Shield, Tag, Building2, ArrowLeft, 
  SlidersHorizontal, ChevronRight, Heart, Menu, User, ClipboardList,
  RotateCcw, Settings, HelpCircle, LogOut, Gift, Percent, Bell,
  Paintbrush, HardHat, Ruler, Wind, TreeDeciduous, ShieldCheck, Hammer,
  ChevronDown, Users, Store, MessageSquare, Briefcase, Newspaper, LogIn,
  AlertTriangle, Info, Euro, Filter, Check, Loader2, Award
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useBuyer } from '../../contexts/BuyerContext';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import UniversalBanner from '../../components/layout/UniversalBanner';
import BetaBanner from '../../components/ui/BetaBanner';
import UnifiedFilterSystem, { FilterButton, FilterContextPhrase } from '../../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../../components/demo/DemoModeProvider';
import MarketplaceCatalogueMenu, { CatalogueMenuButton } from '../../components/marketplace/MarketplaceCatalogueMenu';

import { API_URL } from '../../config/api';

// 16 Rayons avec icônes - mapping par slug ou ID complet
const RAYONS_ICONS = {
  'rayon-electricite': Zap,
  'rayon-plomberie': Droplets,
  'rayon-jardin-paysage': TreeDeciduous,
  'rayon-piscine-spa': Droplets,
  'rayon-peinture-decoration': Paintbrush,
  'rayon-maconnerie-gros-oeuvre': HardHat,
  'rayon-renovation-interieure': Hammer,
  'rayon-carrelage-revetement': Layers,
  'rayon-menuiserie': Ruler,
  'rayon-chauffage': Flame,
  'rayon-couverture-toiture': Building2,
  'rayon-isolation': ShieldCheck,
  'rayon-climatisation-ventilation': Wind,
  'rayon-facade-enduit': Building2,
  'rayon-energie-solaire': Zap,
  'rayon-outillage-equipement': Wrench,
  // Fallbacks par slug simple
  'electricite': Zap,
  'plomberie': Droplets,
  'jardin': TreeDeciduous,
  'piscine': Droplets,
  'peinture': Paintbrush,
  'maconnerie': HardHat,
  'renovation': Hammer,
  'carrelage': Layers,
  'menuiserie': Ruler,
  'chauffage': Flame,
  'couverture': Building2,
  'isolation': ShieldCheck,
  'climatisation': Wind,
  'facade': Building2,
  'solaire': Zap,
  'outillage': Wrench,
};

const RAYONS_COLORS = {
  'rayon-electricite': '#F59E0B',
  'rayon-plomberie': '#3B82F6',
  'rayon-jardin-paysage': '#22C55E',
  'rayon-piscine-spa': '#06B6D4',
  'rayon-peinture-decoration': '#EC4899',
  'rayon-maconnerie-gros-oeuvre': '#EA580C',
  'rayon-renovation-interieure': '#6366F1',
  'rayon-carrelage-revetement': '#10B981',
  'rayon-menuiserie': '#D97706',
  'rayon-chauffage': '#EF4444',
  'rayon-couverture-toiture': '#8B5CF6',
  'rayon-isolation': '#22C55E',
  'rayon-climatisation-ventilation': '#06B6D4',
  'rayon-facade-enduit': '#A855F7',
  'rayon-energie-solaire': '#F59E0B',
  'rayon-outillage-equipement': '#64748B',
  // Fallbacks
  'electricite': '#F59E0B',
  'plomberie': '#3B82F6',
  'jardin': '#22C55E',
  'piscine': '#06B6D4',
  'peinture': '#EC4899',
  'maconnerie': '#EA580C',
  'renovation': '#6366F1',
  'carrelage': '#10B981',
  'menuiserie': '#D97706',
  'chauffage': '#EF4444',
  'couverture': '#8B5CF6',
  'isolation': '#22C55E',
  'climatisation': '#06B6D4',
  'facade': '#A855F7',
  'solaire': '#F59E0B',
  'outillage': '#64748B',
};

// ══════════════════════════════════════════════════════════════════════════════
// MARQUES PAR RAYON - Contextuelles selon la catégorie sélectionnée
// ══════════════════════════════════════════════════════════════════════════════

const BRANDS_BY_RAYON = {
  // Électricité
  'rayon-electricite': ['Legrand', 'Schneider Electric', 'Hager', 'ABB', 'Siemens', 'Gewiss', 'Arnould', 'Debflex'],
  // Plomberie
  'rayon-plomberie': ['Grohe', 'Hansgrohe', 'Geberit', 'Ideal Standard', 'Jacob Delafon', 'Roca', 'Nicoll', 'Wirquin'],
  // Jardin & Paysage
  'rayon-jardin-paysage': ['Husqvarna', 'Stihl', 'Gardena', 'Bosch Jardin', 'Ryobi', 'Kärcher', 'Wolf', 'Fiskars'],
  // Piscine & Spa
  'rayon-piscine-spa': ['Dolphin', 'Zodiac', 'Hayward', 'Intex', 'Bestway', 'Desjoyaux', 'Waterair', 'Maytronics'],
  // Peinture & Décoration
  'rayon-peinture-decoration': ['Tollens', 'V33', 'Dulux Valentine', 'Ripolin', 'Sikkens', 'Zolpan', 'Libéron', 'Syntilor'],
  // Maçonnerie & Gros œuvre
  'rayon-maconnerie-gros-oeuvre': ['Weber', 'Parex Lanko', 'Sika', 'Mapei', 'Knauf', 'Lafarge', 'PRB', 'Bostik'],
  // Rénovation intérieure
  'rayon-renovation-interieure': ['Placo', 'Knauf', 'Fermacell', 'Siniat', 'Gyproc', 'Rigips', 'Saint-Gobain'],
  // Carrelage & Revêtement
  'rayon-carrelage-revetement': ['Marazzi', 'Porcelanosa', 'Castorama Pro', 'Novoceram', 'Ragno', 'Refin', 'Villeroy & Boch'],
  // Menuiserie
  'rayon-menuiserie': ['Velux', 'Tryba', 'K-Line', 'Lapeyre', 'Leroy Merlin Pro', 'Jeld-Wen', 'Bel\'M', 'Bubendorff'],
  // Chauffage
  'rayon-chauffage': ['Atlantic', 'De Dietrich', 'Saunier Duval', 'Chaffoteaux', 'Viessmann', 'Bosch Thermotechnologie', 'Frisquet', 'Chappée'],
  // Couverture & Toiture
  'rayon-couverture-toiture': ['Monier', 'Edilians', 'Terreal', 'Wienerberger', 'Koramic', 'Eternit', 'Onduline', 'Icopal'],
  // Isolation
  'rayon-isolation': ['Rockwool', 'Isover', 'Knauf Insulation', 'Ursa', 'Recticel', 'Soprema', 'Actis', 'Efisol'],
  // Climatisation & Ventilation
  'rayon-climatisation-ventilation': ['Daikin', 'Mitsubishi Electric', 'Atlantic Climatisation', 'Toshiba', 'LG', 'Samsung', 'Panasonic', 'Aldes'],
  // Façade & Enduit
  'rayon-facade-enduit': ['Weber', 'Parex Lanko', 'PRB', 'STO', 'Baumit', 'Zolpan', 'Tollens Façade', 'Caparol'],
  // Énergie solaire
  'rayon-energie-solaire': ['SunPower', 'Longi', 'JA Solar', 'Canadian Solar', 'Trina Solar', 'Enphase', 'SolarEdge', 'Dualsun'],
  // Outillage & Équipement
  'rayon-outillage-equipement': ['Bosch Professional', 'Makita', 'DeWalt', 'Milwaukee', 'Festool', 'Hilti', 'Metabo', 'Stanley'],
};

// Marques génériques (affichées quand aucun rayon sélectionné)
const BRANDS_GENERIC = [
  'Bosch Professional', 'Makita', 'DeWalt', 'Grohe', 'Legrand', 
  'Schneider Electric', 'Atlantic', 'Rockwool', 'Velux', 'Dolphin'
];

// ══════════════════════════════════════════════════════════════════════════════
// BARRE DE RECHERCHE AVEC AUTOCOMPLETE 3 NIVEAUX
// ══════════════════════════════════════════════════════════════════════════════

function SearchBarAutocomplete({ onSearch, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimer = useRef(null);

  // Fetch autocomplete suggestions
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketplace/search/categories?q=${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.slice(0, 10)); // Max 10 suggestions
      }
    } catch (e) {
      console.error('Autocomplete error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 200);
    return () => clearTimeout(debounceTimer.current);
  }, [query, fetchSuggestions]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSelectSuggestion(suggestions[selectedIndex]);
      } else {
        handleSubmit();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setShowSuggestions(false);
    setQuery(suggestion.match);
    onSearch({
      query: suggestion.match,
      rayon_id: suggestion.rayon_id,
      sous_rayon_id: suggestion.sous_rayon_id || null,
      category_id: suggestion.category_id || null,
    });
  };

  const handleSubmit = () => {
    setShowSuggestions(false);
    onSearch({ query });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'rayon': return 'Rayon';
      case 'sous_rayon': return 'Sous-rayon';
      case 'category': return 'Catégorie';
      default: return '';
    }
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher un produit, une marque, un rayon..."
          className="w-full pl-12 pr-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:border-[#F97316] focus:outline-none focus:ring-2 focus:ring-[#F97316]/20 transition-all"
          data-testid="marketplace-search-input"
        />
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        {loading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={20} />
        )}
        {!loading && query && (
          <button
            onClick={() => { setQuery(''); setSuggestions([]); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            ref={suggestionsRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
            data-testid="search-suggestions"
          >
            {suggestions.map((s, idx) => {
              const Icon = RAYONS_ICONS[s.rayon_id] || Package;
              const color = RAYONS_COLORS[s.rayon_id] || '#6B7280';
              
              return (
                <button
                  key={`${s.type}-${s.rayon_id}-${s.sous_rayon_id || ''}-${s.category_id || ''}-${idx}`}
                  onClick={() => handleSelectSuggestion(s)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                    selectedIndex === idx ? 'bg-gray-50' : ''
                  }`}
                  data-testid={`suggestion-${idx}`}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    <Icon size={20} style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{s.match}</p>
                    <p className="text-xs text-gray-500">
                      {s.type === 'rayon' && s.rayon_name}
                      {s.type === 'sous_rayon' && `${s.rayon_name} › ${s.sous_rayon_name}`}
                      {s.type === 'category' && `${s.rayon_name} › ${s.sous_rayon_name} › ${s.category_name}`}
                    </p>
                  </div>
                  <span 
                    className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{ backgroundColor: `${color}15`, color }}
                  >
                    {getTypeLabel(s.type)}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SIDEBAR FILTRES - DESKTOP (Style Leroy Merlin / Amazon)
// ══════════════════════════════════════════════════════════════════════════════

function FilterSidebar({ 
  rayons, 
  filters, 
  onFilterChange, 
  onReset,
  productCount 
}) {
  const [expandedSections, setExpandedSections] = useState({
    rayons: true,
    brands: true,
    price: true,
    rating: false,
    options: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Marques contextuelles selon le rayon sélectionné
  const availableBrands = filters.rayon_id 
    ? (BRANDS_BY_RAYON[filters.rayon_id] || BRANDS_GENERIC)
    : BRANDS_GENERIC;

  const priceRanges = [
    { id: 'all', label: 'Tous les prix', min: 0, max: null },
    { id: '0-50', label: 'Moins de 50 €', min: 0, max: 50 },
    { id: '50-100', label: '50 € - 100 €', min: 50, max: 100 },
    { id: '100-250', label: '100 € - 250 €', min: 100, max: 250 },
    { id: '250-500', label: '250 € - 500 €', min: 250, max: 500 },
    { id: '500+', label: 'Plus de 500 €', min: 500, max: null },
  ];

  const ratingOptions = [
    { value: 4, label: '4 étoiles et plus' },
    { value: 3, label: '3 étoiles et plus' },
    { value: 2, label: '2 étoiles et plus' },
  ];

  return (
    <aside className="w-64 flex-shrink-0 hidden lg:block" data-testid="filter-sidebar">
      <div className="sticky top-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span className="text-[16px] font-semibold text-[#0F172A] flex items-center gap-2">
            <Filter size={18} className="text-[#1E40AF]" />
            Filtres
          </span>
          {(filters.rayon_id || filters.brand || filters.price !== 'all' || filters.min_rating || filters.free_shipping || filters.in_stock) && (
            <button
              onClick={onReset}
              className="text-xs text-[#F97316] hover:underline font-medium"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Rayons Section */}
        <div className="border-b border-gray-100">
          <button
            onClick={() => toggleSection('rayons')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <span className="font-semibold text-gray-900">Rayons</span>
            <ChevronDown 
              size={18} 
              className={`text-gray-400 transition-transform ${expandedSections.rayons ? 'rotate-180' : ''}`} 
            />
          </button>
          <AnimatePresence>
            {expandedSections.rayons && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 space-y-1 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => onFilterChange({ rayon_id: null, sous_rayon_id: null, category_id: null })}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      !filters.rayon_id ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Layers size={16} />
                    Tous les rayons
                  </button>
                  {rayons.map(rayon => {
                    const Icon = RAYONS_ICONS[rayon.id] || Package;
                    const isSelected = filters.rayon_id === rayon.id;
                    return (
                      <button
                        key={rayon.id}
                        onClick={() => onFilterChange({ 
                          rayon_id: rayon.id, 
                          sous_rayon_id: null, 
                          category_id: null 
                        })}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                          isSelected ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Icon size={16} style={{ color: isSelected ? 'white' : RAYONS_COLORS[rayon.id] }} />
                        {rayon.name}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Marques Section - Contextuelle selon le rayon */}
        <div className="border-b border-gray-100">
          <button
            onClick={() => toggleSection('brands')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Marques</span>
              {filters.rayon_id && (
                <span className="text-xs text-gray-500">
                  ({rayons.find(r => r.id === filters.rayon_id)?.name || 'Rayon'})
                </span>
              )}
            </div>
            <ChevronDown 
              size={18} 
              className={`text-gray-400 transition-transform ${expandedSections.brands ? 'rotate-180' : ''}`} 
            />
          </button>
          <AnimatePresence>
            {expandedSections.brands && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 space-y-1 max-h-48 overflow-y-auto">
                  <button
                    onClick={() => onFilterChange({ brand: null })}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      !filters.brand ? 'bg-[#8B5CF6] text-white' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <Tag size={14} />
                    Toutes les marques
                  </button>
                  {availableBrands.map(brand => (
                    <button
                      key={brand}
                      onClick={() => onFilterChange({ brand })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        filters.brand === brand ? 'bg-[#8B5CF6] text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Prix Section */}
        <div className="border-b border-gray-100">
          <button
            onClick={() => toggleSection('price')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <span className="font-semibold text-gray-900">Prix</span>
            <ChevronDown 
              size={18} 
              className={`text-gray-400 transition-transform ${expandedSections.price ? 'rotate-180' : ''}`} 
            />
          </button>
          <AnimatePresence>
            {expandedSections.price && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 space-y-1">
                  {priceRanges.map(range => (
                    <button
                      key={range.id}
                      onClick={() => onFilterChange({ 
                        price: range.id,
                        min_price: range.min,
                        max_price: range.max
                      })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        filters.price === range.id ? 'bg-[#F97316] text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {filters.price === range.id && <Check size={14} />}
                      {range.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Note Section */}
        <div className="border-b border-gray-100">
          <button
            onClick={() => toggleSection('rating')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <span className="font-semibold text-gray-900">Note client</span>
            <ChevronDown 
              size={18} 
              className={`text-gray-400 transition-transform ${expandedSections.rating ? 'rotate-180' : ''}`} 
            />
          </button>
          <AnimatePresence>
            {expandedSections.rating && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 space-y-2">
                  {ratingOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => onFilterChange({ min_rating: filters.min_rating === opt.value ? null : opt.value })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        filters.min_rating === opt.value ? 'bg-amber-100 text-amber-800' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            size={14} 
                            className={i < opt.value ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} 
                          />
                        ))}
                      </div>
                      <span>et plus</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Options Section */}
        <div>
          <button
            onClick={() => toggleSection('options')}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <span className="font-semibold text-gray-900">Options</span>
            <ChevronDown 
              size={18} 
              className={`text-gray-400 transition-transform ${expandedSections.options ? 'rotate-180' : ''}`} 
            />
          </button>
          <AnimatePresence>
            {expandedSections.options && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-3 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.free_shipping || false}
                      onChange={(e) => onFilterChange({ free_shipping: e.target.checked || null })}
                      className="w-5 h-5 rounded border-gray-300 text-[#22C55E] focus:ring-[#22C55E]"
                    />
                    <div className="flex items-center gap-2">
                      <Truck size={16} className="text-[#22C55E]" />
                      <span className="text-sm text-gray-700">Livraison gratuite</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.in_stock || false}
                      onChange={(e) => onFilterChange({ in_stock: e.target.checked || null })}
                      className="w-5 h-5 rounded border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
                    />
                    <div className="flex items-center gap-2">
                      <Package size={16} className="text-[#1E40AF]" />
                      <span className="text-sm text-gray-700">En stock uniquement</span>
                    </div>
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </aside>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PANNEAU FILTRES MOBILE (Slide-in)
// ══════════════════════════════════════════════════════════════════════════════

function MobileFilterPanel({ isOpen, onClose, rayons, filters, onFilterChange, onReset }) {
  if (!isOpen) return null;

  // Marques contextuelles selon le rayon sélectionné
  const availableBrands = filters.rayon_id 
    ? (BRANDS_BY_RAYON[filters.rayon_id] || BRANDS_GENERIC)
    : BRANDS_GENERIC;

  const priceRanges = [
    { id: 'all', label: 'Tous les prix', min: 0, max: null },
    { id: '0-50', label: 'Moins de 50 €', min: 0, max: 50 },
    { id: '50-100', label: '50 € - 100 €', min: 50, max: 100 },
    { id: '100-250', label: '100 € - 250 €', min: 100, max: 250 },
    { id: '250-500', label: '250 € - 500 €', min: 250, max: 500 },
    { id: '500+', label: 'Plus de 500 €', min: 500, max: null },
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        exit={{ x: '-100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col"
      >
        {/* Header */}
        <div className="bg-[#1E40AF] px-4 py-4 flex items-center justify-between">
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <SlidersHorizontal size={20} />
            Filtres
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Rayons */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 mb-3">Rayons</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              <button
                onClick={() => onFilterChange({ rayon_id: null, brand: null })}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  !filters.rayon_id ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100'
                }`}
              >
                Tous les rayons
              </button>
              {rayons.map(rayon => {
                const Icon = RAYONS_ICONS[rayon.id] || Package;
                return (
                  <button
                    key={rayon.id}
                    onClick={() => onFilterChange({ rayon_id: rayon.id, brand: null })}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      filters.rayon_id === rayon.id ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={16} style={{ color: filters.rayon_id === rayon.id ? 'white' : RAYONS_COLORS[rayon.id] }} />
                    {rayon.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Marques - Contextuelles */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Tag size={18} className="text-[#8B5CF6]" />
              Marques
              {filters.rayon_id && (
                <span className="text-xs text-gray-500 font-normal">
                  ({rayons.find(r => r.id === filters.rayon_id)?.name})
                </span>
              )}
            </h3>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              <button
                onClick={() => onFilterChange({ brand: null })}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  !filters.brand ? 'bg-[#8B5CF6] text-white' : 'hover:bg-gray-100'
                }`}
              >
                Toutes les marques
              </button>
              {availableBrands.map(brand => (
                <button
                  key={brand}
                  onClick={() => onFilterChange({ brand })}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    filters.brand === brand ? 'bg-[#8B5CF6] text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {brand}
                </button>
              ))}
            </div>
          </div>

          {/* Prix */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Euro size={18} className="text-[#F97316]" />
              Prix
            </h3>
            <div className="space-y-1">
              {priceRanges.map(range => (
                <button
                  key={range.id}
                  onClick={() => onFilterChange({ 
                    price: range.id,
                    min_price: range.min,
                    max_price: range.max
                  })}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    filters.price === range.id ? 'bg-[#F97316] text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Star size={18} className="text-amber-400" />
              Note client
            </h3>
            <div className="space-y-2">
              {[4, 3, 2].map(rating => (
                <button
                  key={rating}
                  onClick={() => onFilterChange({ min_rating: filters.min_rating === rating ? null : rating })}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                    filters.min_rating === rating ? 'bg-amber-100 text-amber-800' : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                    ))}
                  </div>
                  <span>et plus</span>
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="p-4">
            <h3 className="font-bold text-gray-900 mb-3">Options</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.free_shipping || false}
                  onChange={(e) => onFilterChange({ free_shipping: e.target.checked || null })}
                  className="w-5 h-5 rounded border-gray-300 text-[#22C55E] focus:ring-[#22C55E]"
                />
                <span className="text-sm text-gray-700">Livraison gratuite</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.in_stock || false}
                  onChange={(e) => onFilterChange({ in_stock: e.target.checked || null })}
                  className="w-5 h-5 rounded border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
                />
                <span className="text-sm text-gray-700">En stock uniquement</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onReset}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-[#EA580C] transition-colors"
            >
              Voir les résultats
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CARTE PRODUIT
// ══════════════════════════════════════════════════════════════════════════════

function ProductCard({ product, index, onAddToCart, onToggleFavorite, isFavorite }) {
  const navigate = useNavigate();
  const { requireAuth } = useDemoMode();
  const isDemo = product.is_demo;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (requireAuth('ajouter ce produit au panier')) {
      onAddToCart(product);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (requireAuth('ajouter aux favoris')) {
      onToggleFavorite(product);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, delay: index * 0.02 }}
      className="bg-white rounded-lg overflow-hidden border border-gray-200 relative group hover:shadow-lg transition-shadow"
      data-testid={`product-card-${product.id}`}
    >
      {/* Bouton Favoris */}
      <button
        onClick={handleToggleFavorite}
        className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:bg-white transition-colors"
        data-testid={`favorite-btn-${product.id}`}
      >
        <Heart 
          size={16} 
          className={isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'} 
        />
      </button>
      
      {/* Image - Format carré uniforme */}
      <div 
        className="relative aspect-square bg-gray-50 cursor-pointer overflow-hidden"
        onClick={() => navigate(`/produits/${product.id}`)}
      >
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <Package size={40} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge TEST */}
        {isDemo && (
          <div className="absolute top-1 left-1 bg-amber-500 text-white px-2 py-0.5 text-[10px] font-bold rounded-sm flex items-center gap-1">
            <Info size={10} />
            TEST
          </div>
        )}
        
        {/* Badge Livraison Gratuite */}
        {product.delivery_free && (
          <div className="absolute bottom-1 left-1 bg-green-600 text-white px-2 py-0.5 text-[10px] font-bold rounded-sm flex items-center gap-1">
            <Truck size={10} />
            Gratuite
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Rayon badge */}
        {product.rayon_name && (
          <span 
            className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full mb-1"
            style={{ 
              backgroundColor: `${RAYONS_COLORS[product.rayon_id] || '#6B7280'}15`,
              color: RAYONS_COLORS[product.rayon_id] || '#6B7280'
            }}
          >
            {product.rayon_name}
          </span>
        )}
        
        <h3 
          className="text-sm text-gray-900 line-clamp-2 leading-tight mb-1 cursor-pointer hover:text-[#F97316]"
          onClick={() => navigate(`/produits/${product.id}`)}
        >
          {product.name}
        </h3>

        {/* Marque */}
        {product.brand && (
          <p className="text-xs text-gray-500 mb-1">{product.brand}</p>
        )}

        {/* Note */}
        {product.rating > 0 && (
          <div className="flex items-center gap-1 mb-1">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  className={i < Math.floor(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                />
              ))}
            </div>
            <span className="text-[10px] text-blue-600">({product.reviews_count || 0})</span>
          </div>
        )}

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-lg font-bold text-[#F97316]">
            {formatPrice(product.price)}
          </span>
        </div>

        {/* Stock */}
        <p className={`text-xs mb-2 ${product.in_stock ? 'text-green-600' : 'text-red-500'}`}>
          {product.stock_status || (product.in_stock ? 'En stock' : 'Rupture de stock')}
        </p>
        
        <button
          onClick={handleAddToCart}
          disabled={!product.in_stock}
          className="w-full py-2 px-4 bg-white hover:bg-[#F97316] active:bg-[#E8650A] border-2 border-[#F97316] rounded-lg text-sm font-semibold text-[#F97316] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`add-to-cart-${product.id}`}
        >
          Ajouter au panier
        </button>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// NAVIGATION MOBILE EN BAS
// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE MARKETPLACE
// ══════════════════════════════════════════════════════════════════════════════

const DEMO_PRODUCTS_MARKETPLACE = [
  { id: 'demo-mp-1', name: 'Perceuse-visseuse Bosch Professional GSR 18V-55', image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400', rayon_id: 'rayon-outillage-equipement', rayon_name: 'Outillage', brand: 'Bosch Professional', price: 189, rating: 4.8, reviews_count: 342, in_stock: true, delivery_free: true, stock_status: 'En stock' },
  { id: 'demo-mp-2', name: 'Câble électrique rigide H07V-U 1,5mm² — bobine 100m', image: null, rayon_id: 'rayon-electricite', rayon_name: 'Électricité', brand: 'Nexans', price: 45, rating: 4.7, reviews_count: 203, in_stock: true, delivery_free: false, stock_status: 'En stock' },
  { id: 'demo-mp-3', name: 'Niveau laser croix Bosch GCL 2-50 G', image: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=400', rayon_id: 'rayon-outillage-equipement', rayon_name: 'Outillage', brand: 'Bosch Professional', price: 299, rating: 4.9, reviews_count: 156, in_stock: true, delivery_free: true, stock_status: 'En stock' },
  { id: 'demo-mp-4', name: 'Lot de 500 vis inox tête fraisée 4×40mm', image: null, rayon_id: 'rayon-outillage-equipement', rayon_name: 'Outillage', brand: 'QuincailleriePro', price: 24, rating: 4.5, reviews_count: 89, in_stock: true, delivery_free: false, stock_status: 'En stock' },
  { id: 'demo-mp-5', name: 'Robinet mitigeur cuisine Grohe BauEdge chromé', image: null, rayon_id: 'rayon-plomberie', rayon_name: 'Plomberie', brand: 'Grohe', price: 89, rating: 4.6, reviews_count: 91, in_stock: true, delivery_free: false, stock_status: 'En stock' },
  { id: 'demo-mp-6', name: 'Scie circulaire 185mm DeWalt DCS570P2 sans fil', image: null, rayon_id: 'rayon-outillage-equipement', rayon_name: 'Outillage', brand: 'DeWalt', price: 349, rating: 4.8, reviews_count: 124, in_stock: true, delivery_free: true, stock_status: 'En stock' },
  { id: 'demo-mp-7', name: 'Enduit de lissage Parexgroup 25kg — sac poudre', image: null, rayon_id: 'rayon-peinture-decoration', rayon_name: 'Peinture & Déco', brand: 'Parexgroup', price: 18, rating: 4.3, reviews_count: 67, in_stock: true, delivery_free: false, stock_status: 'En stock' },
  { id: 'demo-mp-8', name: 'Radiateur à inertie digitale Campa CAMPAVER 1500W', image: null, rayon_id: 'rayon-chauffage', rayon_name: 'Chauffage', brand: 'Campa', price: 279, rating: 4.4, reviews_count: 58, in_stock: true, delivery_free: true, stock_status: 'En stock' },
  { id: 'demo-mp-9', name: 'Carrelage grès cérame 60×60 effet béton gris — m²', image: null, rayon_id: 'rayon-carrelage-revetement', rayon_name: 'Carrelage', brand: 'Ceramsol', price: 22, rating: 4.6, reviews_count: 178, in_stock: true, delivery_free: false, stock_status: 'En stock' },
  { id: 'demo-mp-10', name: 'Panneau isolant laine de verre Isover 100mm — m²', image: null, rayon_id: 'rayon-isolation', rayon_name: 'Isolation', brand: 'Isover', price: 12, rating: 4.5, reviews_count: 93, in_stock: true, delivery_free: false, stock_status: 'En stock' },
];

export default function MarketplaceProduitsPage({ embedded = false, filter }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // États principaux
  const [products, setProducts] = useState(DEMO_PRODUCTS_MARKETPLACE);
  const [rayons, setRayons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);

  // Filtres
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    rayon_id: searchParams.get('rayon') || null,
    sous_rayon_id: searchParams.get('sous_rayon') || null,
    category_id: searchParams.get('category') || null,
    brand: searchParams.get('brand') || null,
    price: searchParams.get('price') || 'all',
    min_price: searchParams.get('min_price') ? parseFloat(searchParams.get('min_price')) : null,
    max_price: searchParams.get('max_price') ? parseFloat(searchParams.get('max_price')) : null,
    min_rating: searchParams.get('rating') ? parseInt(searchParams.get('rating')) : null,
    free_shipping: searchParams.get('free_shipping') === 'true',
    in_stock: searchParams.get('in_stock') === 'true',
    sort: searchParams.get('sort') || 'relevance'
  });

  // États des panels
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCatalogueMenu, setShowCatalogueMenu] = useState(false);

  // Contexte acheteur
  const { 
    cart, favorites, cartCount, favoritesCount,
    addToCart: contextAddToCart, 
    toggleFavorite: contextToggleFavorite, 
    isFavorite: contextIsFavorite,
  } = useBuyer();

  // Charger les rayons au démarrage
  useEffect(() => {
    fetchRayons();
  }, []);

  // Charger les produits quand les filtres changent
  useEffect(() => {
    fetchProducts();
    updateSearchParams();
  }, [filters]);

  const fetchRayons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/rayons`);
      if (res.ok) {
        const data = await res.json();
        setRayons(data);
      }
    } catch (e) {
      console.error('Error fetching rayons:', e);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.set('search', filters.search);
      if (filters.rayon_id) params.set('rayon_id', filters.rayon_id);
      if (filters.sous_rayon_id) params.set('sous_rayon_id', filters.sous_rayon_id);
      if (filters.category_id) params.set('category_id', filters.category_id);
      if (filters.brand) params.set('brand', filters.brand);
      if (filters.min_price !== null) params.set('min_price', filters.min_price.toString());
      if (filters.max_price !== null) params.set('max_price', filters.max_price.toString());
      if (filters.min_rating) params.set('min_rating', filters.min_rating.toString());
      if (filters.free_shipping) params.set('free_shipping', 'true');
      if (filters.in_stock) params.set('in_stock', 'true');
      if (filters.sort) params.set('sort', filters.sort);
      params.set('per_page', '24');

      const res = await fetch(`${API_URL}/api/marketplace/products?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.products && data.products.length > 0) {
          setProducts(data.products);
          setTotalProducts(data.total || 0);
        } else {
          setProducts(DEMO_PRODUCTS_MARKETPLACE);
          setTotalProducts(DEMO_PRODUCTS_MARKETPLACE.length);
        }
      } else {
        setProducts(DEMO_PRODUCTS_MARKETPLACE);
        setTotalProducts(DEMO_PRODUCTS_MARKETPLACE.length);
      }
    } catch (e) {
      console.error('Error fetching products:', e);
      setProducts(DEMO_PRODUCTS_MARKETPLACE);
      setTotalProducts(DEMO_PRODUCTS_MARKETPLACE.length);
    } finally {
      setLoading(false);
    }
  };

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('q', filters.search);
    if (filters.rayon_id) params.set('rayon', filters.rayon_id);
    if (filters.sous_rayon_id) params.set('sous_rayon', filters.sous_rayon_id);
    if (filters.category_id) params.set('category', filters.category_id);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.price !== 'all') params.set('price', filters.price);
    if (filters.min_rating) params.set('rating', filters.min_rating.toString());
    if (filters.free_shipping) params.set('free_shipping', 'true');
    if (filters.in_stock) params.set('in_stock', 'true');
    if (filters.sort !== 'relevance') params.set('sort', filters.sort);
    setSearchParams(params, { replace: true });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      rayon_id: null,
      sous_rayon_id: null,
      category_id: null,
      brand: null,
      price: 'all',
      min_price: null,
      max_price: null,
      min_rating: null,
      free_shipping: false,
      in_stock: false,
      sort: 'relevance'
    });
  };

  const handleSearch = ({ query, rayon_id, sous_rayon_id, category_id }) => {
    setFilters(prev => ({
      ...prev,
      search: query || '',
      rayon_id: rayon_id || prev.rayon_id,
      sous_rayon_id: sous_rayon_id || null,
      category_id: category_id || null,
    }));
  };

  const addToCart = (product) => {
    contextAddToCart({
      id: product.id,
      title: product.name,
      price: product.price,
      image: product.image,
      photos: product.image ? [product.image] : []
    }, 1);
  };

  const toggleFavorite = (product) => {
    contextToggleFavorite({
      id: product.id,
      title: product.name,
      price: product.price,
      image: product.image
    });
  };

  // Compteur filtres actifs
  const activeFiltersCount = [
    filters.rayon_id,
    filters.brand,
    filters.price !== 'all',
    filters.min_rating,
    filters.free_shipping,
    filters.in_stock
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif]" data-testid="marketplace-produits-page">
      {/* Header Unifié - avec autocomplete marketplace */}
      {!embedded && <UnifiedHeader
        onSearch={handleSearch}
        cartCount={cartCount}
        showCart={true}
        marketplaceMode={true}
        initialSearchQuery={filters.search}
        onOpenFilters={() => setShowMobileFilters(true)}
      />}

      {/* Navigation Principale */}
      {!embedded && <MainNavigation
        onOpenRayonsMenu={() => setShowCatalogueMenu(true)}
        onOpenFilters={() => setShowMobileFilters(true)}
      />}

      {/* Spacer Mobile - 44px pour nav principale */}
      <div className="h-11 md:hidden" />

      {/* Spacer Desktop/Tablet - pour compenser les headers fixed */}
      <div className="hidden md:block" style={{ height: '100px' }} />

      {/* ═══ HERO BANNER MARKETPLACE — Mobile uniquement ═══ */}
      <div className="lg:hidden bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        
        {/* Version MOBILE - Bannière compacte */}
        <div className="md:hidden px-3 py-4 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1.5">
              <Store size={16} className="text-yellow-300 flex-shrink-0" />
              <h1 className="text-sm font-black text-white tracking-wide whitespace-nowrap">
                MARKETPLACE DU BÂTIMENT
              </h1>
              <Store size={16} className="text-yellow-300 flex-shrink-0" />
            </div>
            <p className="text-white/90 text-xs">
              Achats <span className="font-bold text-white">sans intermédiaires</span> et <span className="font-bold text-white">commissions</span>
            </p>
          </div>
        </div>
        
      </div>

      {/* Badges de confiance - Masqué sur Desktop */}

      {/* Filtres actifs - Ligne discrète */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto bg-gray-50" style={{ scrollbarWidth: 'none' }}>
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              "{filters.search}"
              <button onClick={() => handleFilterChange({ search: '' })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.rayon_id && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {rayons.find(r => r.id === filters.rayon_id)?.name}
              <button onClick={() => handleFilterChange({ rayon_id: null, brand: null })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.brand && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              {filters.brand}
              <button onClick={() => handleFilterChange({ brand: null })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.free_shipping && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium whitespace-nowrap">
              Livraison gratuite
              <button onClick={() => handleFilterChange({ free_shipping: false })} className="hover:bg-gray-200 rounded p-0.5 ml-0.5">
                <X size={10} />
              </button>
            </span>
          )}
          <button
            onClick={handleResetFilters}
            className="text-xs text-gray-500 hover:text-red-600 font-medium whitespace-nowrap ml-1"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* Résultats Header simplifié */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2 md:py-3">
          <div>
            <span className="font-bold text-lg text-gray-900">{totalProducts}</span>
            <span className="text-gray-600 ml-1">résultat{totalProducts !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Contenu Principal - Sans sidebar, filtres via bouton uniquement */}
      <div className="max-w-7xl mx-auto px-4 py-1 md:py-6">
        {/* Grille Produits */}
        <main>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Package size={56} className="mx-auto text-gray-300 mb-4" />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Aucun produit trouvé</h3>
              <p className="text-gray-500 mb-6">Essaie de modifier tes critères de recherche</p>
              <button 
                onClick={handleResetFilters} 
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E40AF] text-white rounded-xl font-medium hover:bg-[#1E3A8A] transition-colors"
              >
                Voir tous les produits
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {products.map((product, index) => (
                <ProductCard 
                  key={product.id} 
                    product={product} 
                    index={index}
                    onAddToCart={addToCart}
                    onToggleFavorite={toggleFavorite}
                    isFavorite={contextIsFavorite(product.id)}
                  />
                ))}
              </div>
            )}
          </main>
      </div>

      {/* Trust Footer */}
      <div className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { icon: Truck, text: 'Livraison rapide', subtext: 'Partout en France' },
              { icon: Shield, text: 'Paiement sécurisé', subtext: '100% protégé' },
              { icon: Tag, text: 'Meilleurs prix', subtext: 'Garantis' },
              { icon: Star, text: 'Qualité pro', subtext: 'Matériaux certifiés' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-xl bg-[#1E40AF]/10 flex items-center justify-center mb-3">
                  <item.icon size={24} className="text-[#1E40AF]" />
                </div>
                <span className="font-semibold text-gray-900">{item.text}</span>
                <span className="text-sm text-gray-500">{item.subtext}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* ══════════════════════════════════════════════════════════════════════════
          SYSTÈME DE FILTRES UNIFIÉ - Amazon/Airbnb Style (identique à Artisans)
      ══════════════════════════════════════════════════════════════════════════ */}
      <UnifiedFilterSystem
        context="marketplace"
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        filters={filters}
        onFiltersChange={handleFilterChange}
        resultsCount={totalProducts}
        onApply={() => setShowMobileFilters(false)}
        rayons={rayons}
        sousRayons={[]}
        marques={[]}
      />
      
      {/* Menu Catalogue Style ManoMano */}
      <MarketplaceCatalogueMenu 
        isOpen={showCatalogueMenu}
        onClose={() => setShowCatalogueMenu(false)}
      />
      
      {/* Footer Global */}
      
      
      {/* Spacer mobile pour navigation */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
